import { fetchMe, fetchKycStatus } from '~/api/me'
import type { PlayerProfileDto, KycStatusDto } from '~/types/api/me'
import type { VipSummaryDto } from '~/types/api/promo'
import { setNoticeUnreadFromMe } from '~/composables/useNotices'
import { applyFundPasswordFromMe } from '~/utils/fundPasswordSession'

/** /me.vip / 俸禄面板 vip → store 形态 */
export function mapVipSummary(vip: VipSummaryDto) {
  const progressPct = Math.min(100, Math.max(0, Number(vip.progress_pct) || 0))
  const isMax = progressPct >= 100
  return {
    level: vip.level,
    name: vip.name || `VIP ${vip.level}`,
    nextLevel: isMax ? vip.level : vip.level + 1,
    progressPct,
    gapAmount: vip.gap_amount || '0',
    currency: vip.currency || '',
    isMax,
    iconUrl: vip.icon_url && !vip.icon_key ? vip.icon_url : '',
  }
}

/**
 * 回填 GET /me：档案 / VIP / 奖励红点 / 资金密码开关 / 站内信未读。
 * 登录成功与持久化恢复共用，避免两处拷贝分叉。
 */
export function applyPlayerMe(
  me: PlayerProfileDto,
  options?: { trustFundPasswordFromMe?: boolean },
) {
  const userStore = useUserStore()
  userStore.setProfile({
    uid: me.player_id,
    nickname: me.username ?? '',
    phone: me.phone ?? '',
    email: me.email ?? '',
  })
  applyFundPasswordFromMe(
    me.fund_password_set,
    options?.trustFundPasswordFromMe ? { trustMeOnly: true } : undefined,
  )
  if (me.vip) userStore.setVip(mapVipSummary(me.vip))
  // KYC 功能开关以 /me.kyc_required 为准（false=全部隐藏 KYC 入口）
  if (typeof me.kyc_required === 'boolean') {
    userStore.kycRequired = me.kyc_required
  }
  userStore.setRewardBadges({
    salary: me.badges?.salary ?? 0,
    task: me.badges?.task ?? 0,
    rebate: me.badges?.rebate ?? 0,
    total: me.badges?.total ?? 0,
  })
  setNoticeUnreadFromMe(me.unread?.notice ?? 0)
}

/** 回填 GET /kyc/status → store（仅 status + 资料；功能开关由 /me.kyc_required 控制） */
export function applyKycStatus(st: KycStatusDto) {
  const userStore = useUserStore()
  userStore.kycStatus = st.status
  if (st.doc_type) {
    // 合并回填：real_name/id_number/expiry 等为提交时本地缓存（接口不回传），不能整体覆盖
    userStore.kycProfile = {
      ...userStore.kycProfile,
      doc_type: st.doc_type,
      reject_reason: st.reject_reason,
      submitted_at: st.submitted_at,
      reviewed_at: st.reviewed_at,
    }
  }
}

/** 并发的 /me 共用同一请求（插件恢复 + 进入「我的」会撞车） */
let meInflight: Promise<PlayerProfileDto> | null = null
let kycInflight: Promise<KycStatusDto> | null = null

export async function refreshPlayerMe(options?: { trustFundPasswordFromMe?: boolean }) {
  if (meInflight) console.debug('[auth] /me refresh joined in-flight request')
  const req =
    meInflight ??
    (meInflight = fetchMe().finally(() => {
      meInflight = null
    }))
  const me = await req
  applyPlayerMe(me, options)
  return me
}

/** 进入应用 / 登录后拉一次 KYC 状态并回填 store；同会话内并发复用同一请求 */
export async function refreshKycStatus() {
  if (kycInflight) console.debug('[auth] /kyc/status refresh joined in-flight request')
  const req =
    kycInflight ??
    (kycInflight = fetchKycStatus().finally(() => {
      kycInflight = null
    }))
  const st = await req
  applyKycStatus(st)
  return st
}
